import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { Text } from '@dutchie/common/components/rebass-compat';

import { ModalContainer, ModalClose } from 'shared/modals';
import { mediaQueries } from 'shared/styles';
import useStores from 'shared/hooks/use-stores';
import IframeHeightOverride from 'components/core/iframe-height-override';

// ECOMM-14792: California SB 540 / DCC compliance. The DCC requires the
// "Responsible Cannabis Use" brochure to be displayed at checkout without requiring a click
// (a link alone does not satisfy the requirement). BrochureImage below renders at the full
// width of the confirmation column with no interaction required, so it is on screen for
// every CA shopper by default. Clicking/tapping it additionally opens an enlarged, scrollable
// view for shoppers who want to read the fine print up close - that view is a readability
// enhancement, not a substitute for the always-visible inline image.
const BROCHURE_IMAGE_SRC = '/images/compliance/ca-sb540-brochure.jpg';
const BROCHURE_ALT_TEXT = 'California DCC Responsible Cannabis Use informational brochure (SB 540)';
// Source PDF the hosted jpg was generated from. Kept as (1) an always-visible secondary link,
// in case the hosted image is too small to read the fine print on some screens, and (2) the
// fallback destination if the hosted image fails to load.
const BROCHURE_PDF_SRC = 'https://cdn.cannabis.ca.gov/wp-content/uploads/sites/2/2024/12/SB-540-color.pdf';

function CaliforniaBrochure() {
  const [isFullSizeOpen, setFullSizeOpen] = useState(false);
  const [hasImageError, setHasImageError] = useState(false);
  const { UI } = useStores();
  const isEmbedded = UI?.isVariant?.('embedded') ?? false;

  // Checkout also renders inside the Dutchie Menu embed iframe. Inside the iframe, vh/vw
  // resolve against the iframe's own box, not the shopper's real viewport, and the host
  // sizes the iframe to the full page height (often 20k+ px). Sizing this overlay with
  // viewport units there would center/clip it against that huge height instead of what the
  // shopper can actually see. When embedded, size to content and report the measured height
  // to the host so the iframe grows to fit, same pattern as the mobile filters drawer
  // (ECOMM-14193) and OrderStatusModal.
  const FullSizeContent = isEmbedded ? EmbeddedFullSizeContent : StandaloneFullSizeContent;

  return (
    <>
      <BrochureWrap>
        {hasImageError ? (
          <BrochureErrorFallback
            href={BROCHURE_PDF_SRC}
            target='_blank'
            rel='noopener noreferrer'
            data-testid='ca-brochure-image-error-fallback'
          >
            View the state informational brochure (PDF)
          </BrochureErrorFallback>
        ) : (
          <>
            <BrochureButton
              type='button'
              onClick={() => setFullSizeOpen(true)}
              aria-label='Enlarge the state informational brochure'
              data-cy='ca-brochure-image'
              data-test='ca-brochure-image'
              data-testid='ca-brochure-image'
            >
              {/* Eagerly loaded: this is the compliance-required brochure, so it must be on
              screen before the shopper can submit the order, not deferred until scroll. */}
              <BrochureImage
                src={BROCHURE_IMAGE_SRC}
                alt={BROCHURE_ALT_TEXT}
                onError={() => {
                  console.error(`[ECOMM-14792] Failed to load CA brochure image: ${BROCHURE_IMAGE_SRC}`);
                  setHasImageError(true);
                }}
              />
            </BrochureButton>
            <ViewFullSizeHint>
              Tap to enlarge or{' '}
              <BrochurePdfLink href={BROCHURE_PDF_SRC} target='_blank' rel='noopener noreferrer'>
                view as PDF
              </BrochurePdfLink>
            </ViewFullSizeHint>
          </>
        )}
      </BrochureWrap>

      <ModalContainer
        isOpen={isFullSizeOpen}
        onRequestClose={() => setFullSizeOpen(false)}
        noPadding
        mobileFullscreen={false}
        data-testid='ca-brochure-modal'
      >
        <FullSizeContent>
          <ModalClose
            onClick={() => setFullSizeOpen(false)}
            color='var(--dcm-modal-close-color, #43474a)'
            top='12px'
            right='12px'
          />
          <FullSizeImageScroll>
            <FullSizeImage src={BROCHURE_IMAGE_SRC} alt={BROCHURE_ALT_TEXT} />
          </FullSizeImageScroll>
        </FullSizeContent>
      </ModalContainer>
    </>
  );
}

export default CaliforniaBrochure;

const BrochureWrap = styled.div`
  margin: 16px 0 0;
`;

const BrochureButton = styled.button`
  appearance: none;
  display: block;
  width: 100%;
  padding: 0;
  margin: 0;
  border: 1px solid var(--dcm-checkout-summary-border-color, #d3dadf);
  border-radius: var(--dcm-radius-md, 8px);
  background: none;
  cursor: pointer;
  overflow: hidden;

  &:focus-visible {
    outline: 2px solid var(--dcm-checkout-link-color, #0b99e6);
    outline-offset: 2px;
  }
`;

const BrochureImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
`;

// Fallback shown if the hosted jpg 404s/is blocked, so CA shoppers always have *some* way
// to reach the brochure instead of silently seeing nothing.
const BrochureErrorFallback = styled.a`
  display: block;
  text-align: center;
  padding: 16px;
  border: 1px solid var(--dcm-checkout-summary-border-color, #d3dadf);
  border-radius: var(--dcm-radius-md, 8px);
  color: var(--dcm-checkout-link-color, #0b99e6);
  font-size: 14px;
  text-decoration: underline;
`;

const BrochurePdfLink = styled.a`
  color: var(--dcm-checkout-link-color, #0b99e6);
  text-decoration: underline;
`;

// #6d747b against a white background is ~4.8:1, which clears WCAG AA (4.5:1) for small
// text; the previous muted token (#909ca7, ~2.8:1) did not.
const ViewFullSizeHint = styled(Text)`
  text-align: center;
  font-size: 13px;
  line-height: 18px;
  color: var(--dcm-checkout-text-color, #6d747b);
  margin-top: 6px;
`;

const fullSizeContentBaseStyles = css`
  position: relative;
  width: 100%;
  max-width: 900px;
  box-sizing: border-box;
  background: var(--dcm-modal-bg, #fff);
  border-radius: var(--dcm-radius-md, 8px);
  padding: 20px;

  @media ${mediaQueries.largePhone} {
    padding: 16px;
  }
`;

// Standalone (non-embedded) checkout: the real browser viewport, so vh-based sizing is
// accurate here and keeps the brochure scrollable within the visible screen.
const StandaloneFullSizeContent = styled.div`
  ${fullSizeContentBaseStyles}
  max-height: calc(100vh - 32px);
  overflow: auto;
`;

// Embedded checkout: content-sized, no viewport units. IframeHeightOverride measures this
// box and tells the host iframe to resize to fit it.
const EmbeddedFullSizeContent = styled(IframeHeightOverride)`
  ${fullSizeContentBaseStyles}
  max-height: none;
  overflow: visible;
`;

// Rendered at its native width (rather than shrunk to the modal/phone width) inside a
// horizontally-scrollable container, so "enlarge" actually makes the dense brochure text
// bigger on phones instead of just re-rendering the same column-width image.
const FullSizeImageScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const FullSizeImage = styled.img`
  display: block;
  min-width: 900px;
  width: 100%;
  height: auto;
`;
